import { type ReactElement, type ReactNode, useMemo, useState } from 'react'
import { useQueryState, parseAsArrayOf, parseAsString } from 'nuqs'
import {
  isDefined,
  isEmpty,
  isNotEmpty,
  type PermissionDefinition,
  type PermissionEntityId,
  type PermissionList,
  type PermissionType,
} from '@terros/common'
import { Stack, type MantineBreakpoint } from '@mantine/core'
import { useContainerBreakpoint } from '@hooks'
import { useTranslation, type GroupedPermissionDefinitions } from '@front/model'
import { Page } from '../page'
import {
  changedPermissionDefinitions,
  CHANGED_PERMISSION_GROUP_KEY,
  filterPermissionsRecord,
  LEGACY_PERMISSION_GROUP_QUERY_KEY,
  requestedCategoryGroups,
  requestedPermissionGroups,
  resolvePermissionGroup,
  resolvePermissionGroups,
} from './util'
import { PermissionToolbar } from './PermissionToolbar'
import { PermissionDefinitionList } from './PermissionDefinitionList'
import { PermissionCategorySidebar } from './PermissionCategorySidebar'
import { PermissionCategoryAccordion } from './PermissionCategoryAccordion'

const SIDEBAR_BREAKPOINT: MantineBreakpoint = 'xs'

type Props = {
  entityId: PermissionEntityId
  defaultRecord: GroupedPermissionDefinitions
  permissionList: PermissionList
  defaultGroup?: string
  groupQueryKey?: string
  refetch?: () => void
  isFetching?: boolean
  stickyToolbar?: boolean
  toolbarActions?: ReactNode
  render: (type: PermissionType, definition: PermissionDefinition) => ReactNode
}

export const PermissionDisplayWrapper = ({
  entityId,
  defaultRecord,
  permissionList,
  refetch,
  isFetching,
  render,
  defaultGroup,
  groupQueryKey = LEGACY_PERMISSION_GROUP_QUERY_KEY,
  stickyToolbar = true,
  toolbarActions,
}: Props): ReactElement => {
  const { t } = useTranslation()
  const [filter, setFilter] = useState('')
  const { ref, isAtLeast: showSidebar } = useContainerBreakpoint(SIDEBAR_BREAKPOINT)

  const [selectedGroups, setSelectedGroups] = useQueryState(groupQueryKey, parseAsArrayOf(parseAsString))
  const [legacyGroup] = useQueryState(LEGACY_PERMISSION_GROUP_QUERY_KEY, parseAsString)

  const filteredGroups = useMemo(() => filterPermissionsRecord(defaultRecord, filter), [defaultRecord, filter])

  const changedDefinitions = useMemo(
    () => changedPermissionDefinitions(filteredGroups, permissionList, entityId),
    [filteredGroups, permissionList, entityId]
  )
  const hasChanges = isNotEmpty(changedDefinitions)

  const { categories, openGroups, currentGroup, currentDefinitions, isChangedSelected } = useMemo(() => {
    const available = Array.from(filteredGroups.keys())
    const requestedGroups = requestedPermissionGroups({
      selected: selectedGroups,
      legacy: legacyGroup,
      fallback: defaultGroup,
    })
    const requestedCategories = requestedCategoryGroups(requestedGroups)
    const selectedGroup = resolvePermissionGroup(available, requestedCategories)

    return {
      categories: available,
      openGroups: resolvePermissionGroups(available, requestedCategories),
      currentGroup: selectedGroup,
      currentDefinitions: isDefined(selectedGroup) ? filteredGroups.get(selectedGroup) : undefined,
      isChangedSelected: requestedGroups?.includes(CHANGED_PERMISSION_GROUP_KEY) ?? false,
    }
  }, [filteredGroups, selectedGroups, legacyGroup, defaultGroup])

  const renderChangedGroup = (): ReactNode => {
    if (hasChanges) return <PermissionDefinitionList definitions={changedDefinitions} render={render} />
    return <Page.Hero icon='check_circle' title={t('No changed permissions')} />
  }

  const renderPermissionGroups = (): ReactNode => {
    if (isEmpty(filteredGroups)) return null
    if (showSidebar) {
      return (
        <PermissionCategorySidebar
          categories={categories}
          currentGroup={isChangedSelected ? CHANGED_PERMISSION_GROUP_KEY : currentGroup}
          onSelect={(category) => setSelectedGroups([category])}
        >
          {isChangedSelected ? (
            renderChangedGroup()
          ) : (
            <PermissionDefinitionList definitions={currentDefinitions} render={render} />
          )}
        </PermissionCategorySidebar>
      )
    }
    if (isChangedSelected) return renderChangedGroup()
    return (
      <PermissionCategoryAccordion
        groups={filteredGroups}
        openGroup={openGroups.at(0) ?? null}
        render={render}
        onChange={(category) => setSelectedGroups(isDefined(category) ? [category] : [])}
      />
    )
  }

  return (
    <Stack pos='relative' ref={ref} w='100%'>
      <PermissionToolbar
        actions={toolbarActions}
        filter={filter}
        isChangedOnly={isChangedSelected}
        isFetching={isFetching}
        refetch={refetch}
        sticky={stickyToolbar}
        onChangedOnlyChange={(isChangedOnly) =>
          setSelectedGroups(isChangedOnly ? [CHANGED_PERMISSION_GROUP_KEY] : null)
        }
        onFilterChange={setFilter}
      />
      {renderPermissionGroups()}
    </Stack>
  )
}
